import cookieHelpers from '../../helpers/cookie-helpers';

const userStore = (apiServices => ({
  strict: process.env.NODE_ENV !== 'production',
  namespaced: true,
  state: {
    user: {},
    userToken: localStorage.getItem('user-token') || '',
    agentAuthToken: localStorage.getItem('agent-auth-token') || '',
    userProspectorStatus: null,
    userVerifiedDeviceCookie: null,
  },
  mutations: {
    setUser: (state, userObj) => {
      state.user = userObj; // eslint-disable-line no-param-reassign
    },
    setUserToken: (state, userToken) => {
      state.userToken = userToken; // eslint-disable-line no-param-reassign
      cookieHelpers.setCookie('user-token', userToken, 30, null, true);
    },
    setLoggedInUser: (state, userdata) => {
      state.loggedInUser = userdata.uk; // eslint-disable-line no-param-reassign
      cookieHelpers.setCookie('user-key', userdata.uk, 30, null, true);
      cookieHelpers.setCookie('agent-key', userdata.ak, 30, null, true);
    },
    unsetUserToken: (state) => {
      state.userToken = ''; // eslint-disable-line no-param-reassign
      cookieHelpers.deleteCookie('user-token');
    },
    unsetLoggedInUser: (state) => {
      state.loggedInUser = ''; // eslint-disable-line no-param-reassign
      cookieHelpers.deleteCookie('user-key');
      cookieHelpers.deleteCookie('agent-key');
    },
    setUserProspectorStatus: (state, userProspectorStatusObj) => {
      state.userProspectorStatus = userProspectorStatusObj;
    },
    setUserDeviceCookie: (state, userVerifiedCookie) => { 
      cookieHelpers.setCookie(userVerifiedCookie.key, userVerifiedCookie.cookie, 180, null, true);
    },
  },
  actions: {
    async logInUser(context, data) {
      return new Promise((resolve, reject) => {
        apiServices.oa.security.userLogin(data)
          .then((response) => {
            if (response.success) {
              context.commit('setUser', response.data);
              context.commit('setUserToken', response.output);

              const userdata = {
                uk: response.data.key,
                ak: response.data.agentkey,
              };

              /* Check user has correct role/s for AgentPortal
                 (Agent or Principal) */
              const hasCorrectRole = response.data.roles
                .find(role => role.roleid === 3 || role.roleid === 11);

              if (hasCorrectRole) {
                context.commit('setLoggedInUser', userdata);
                resolve(response.data);
              } else {
                const errorMsg = `No Portal account found for ${response.data.email}`;
                reject(errorMsg);
              }
            } else {
              reject(response.message);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async logOutUser(context) {
      return new Promise((resolve) => {
        context.commit('unsetUserToken');
        context.commit('unsetLoggedInUser');
        resolve();
      });
    },
    async loadUser(context, userKey) {
      return apiServices.oa.user.getByKey(userKey)
        .then((user) => {
          context.commit('setUser', user);
        });
    },
    async loadUserProspectorStatus(context, userKey) {
      return apiServices.pp.prospector.getAgentsStatus(userKey)
        .then((data) => {
          if (data) {
            context.commit('setUserProspectorStatus', data);
          }
        });
    },
    async userSignUp(context, payload) {
      return new Promise((resolve, reject) => {
        // verify token
        apiServices.oa.security.verifyUserAccount(payload)
          .then((verifiedResponse) => {
            if (verifiedResponse.success) {
              // set password
              apiServices.oa.security.setPassword(payload)
                .then((response) => {
                  if (response.success) {
                    resolve(response.data);
                  } else {
                    console.error(response.message);
                    reject(verifiedResponse.message);
                  }
                })
                .catch((error) => {
                  console.error(error);
                  reject(error);
                });
            } else {
              console.error(verifiedResponse.message);
              reject(verifiedResponse.message);
            }
          })
          .catch((error) => {
            console.error(error);
            reject(error);
          });
      });
    },
    async sendUserVerificationCode(context, data) {
      return new Promise((resolve, reject) => {
        apiServices.oa.security.sendDeviceOTPVerificationCode(data)
          .then((response) => {
            if (response.success) {
              resolve(response.data);
            } else {
              reject(response.message);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async verifyUserVerificationCode(context, data) {
      return new Promise((resolve, reject) => {
        apiServices.oa.security.verifyDeviceOTPVerificationCode(data)
          .then((response) => {
            if (response.success) {
              let userVerifiedDeviceCookie = response.data.userCookie; // eslint-disable-line no-param-reassign
              let accountId = data.accountid;
              let userKey = data.userkey;
              let cookieKey = userKey + '-' + accountId;
              const verifiedDeviceCookie =
              {
                key: cookieKey,
                cookie: userVerifiedDeviceCookie
              }

              context.commit('setUserDeviceCookie', verifiedDeviceCookie);
              resolve(response.data);
            } else {
              reject(response.message);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
  getters: {
    isAuthenticated: (state) => {
      const userToken = cookieHelpers.getCookie('user-token') ? cookieHelpers.getCookie('user-token') : false;
      const logedInUserKey = cookieHelpers.getCookie('user-key');
      const logedInAgentKey = cookieHelpers.getCookie('agent-key');
      let isloggedInUser = false;

      if ((logedInUserKey && state.user.key === logedInUserKey) || (logedInAgentKey && !state.user.key && state.user.agentkey === logedInAgentKey)) {
        isloggedInUser = true;
      }
      return userToken && isloggedInUser;
    },
    isAgent: (state) => {
      let agentRole = false;
      if (state.user.roles) {
        const userRole = state.user.roles.find(role => role.roleid === 3);
        agentRole = !!(userRole && userRole.roleid === 3);
      }
      return agentRole;
    },
    isProspectingAgent: (state) => {
      let agentRole = false;
      if (state.user.roles) {
        const userRole = state.user.roles.find(role => role.roleid === 13);
        agentRole = !!(userRole && userRole.roleid === 13);
      }
      return agentRole;
    },
    isPrincipal: (state) => {
      let agentPrincipalRole = false;
      if (state.user.roles) {
        const principalRole = state.user.roles.find(role => role.roleid === 11);
        agentPrincipalRole = !!(principalRole && principalRole.roleid === 11);
      }
      return agentPrincipalRole;
    },
    isManager: (state) => {
      let agentPrincipalRole = false;
      if (state.user.roles) {
        const principalRole = state.user.roles.find(role => role.roleid === 12);
        agentPrincipalRole = !!(principalRole && principalRole.roleid === 12);
      }
      return agentPrincipalRole;
    },
    hasProspectorService: (state) => {
      if (state.userProspectorStatus
          && state.userProspectorStatus.prospectingStatus.hasProspectorService) {
        return true;
      }
      return false;
    },
    hasCRMIntegration: (state) => {
      if (state.userProspectorStatus
          && state.userProspectorStatus.prospectingStatus.hasCRMConnection) {
        return true;
      }
      return false;
    },
    hasPortalAccount: (state) => {
      let hasPortalAccount = false;
      if (state.user.agentid && state.user.passwordhash) {
        hasPortalAccount = true;
      }
      return hasPortalAccount;
    },
    crmReconnectionRequired: (state) => {
      if (state.userProspectorStatus && state.userProspectorStatus.crmProvider
        && state.userProspectorStatus.crmProvider.enabled
        && state.userProspectorStatus.crmProvider.name === 'MyDesktop'
        && !state.userProspectorStatus.crmProvider.apiAuthStatus) {
        return true;
      }
      return false;
    },
    isPremier: (state) => {
      // For premier agents
      if (state.user.ispremieragent) {
        return true;
      }

      // For premier principals
      if (state.user.premierstatustypeid === 1) {
        return true;
      }

      return false;
    },
    isDeviceVerified: (state) => {

      let accountId = state.user.accountid;
      let userKey = state.user.key;
      if(!accountId) return false;
      let cookieVariable = userKey + '-' + accountId;
      //get cookie from browser 
      let currentCookie = cookieHelpers.getCookie(cookieVariable) || null;
      if(!currentCookie) return false;

      let usersVerfiedCookies = state.user.userverifieddevicecookies;
      let verified = false;
      for(let i = 0; i<usersVerfiedCookies.length; i++) {
        if(usersVerfiedCookies[i].userCookieType == 1 && usersVerfiedCookies[i].userCookie == currentCookie) {
          verified = true;
          break;
        }
      }
      return verified;
    },
    isInTestBucket: state => state.user
    && state.user.key
  && [
    '0C6BCC73-EED7-42DD-9E76-CD3882D6DD03', // Steven Fan (667)
    '47B03E69-7DE2-476A-8E20-B8207BB0A424', // Cameron Vurovecz (3586)
    '07509A73-16E4-464E-A75F-4B05BE7DB38E', // Mike Lao (8770)
    'DDE85EF2-8C4D-4ACD-BCE7-2B59E5D77F25', // Julian Conte (269948)
    '8FFAE84E-94B5-450E-B7B9-61DC8097DA71', // Leigh Hall (3140)
    // 'F17B0D84-EA1A-4AB7-BC89-EC7731305BB3', // Tony Alessandrino (270293)
    'c4888865-74b2-4f05-a8e5-af1e001c85e1', // Patrick McCarthy (480281)
    '925b498a-357a-4044-ae6e-83d7c3237355', // Karen Thompson (673645)
    '50e13c3d-e885-404a-ad15-164358acb5a2', // Anne Grobler (321889)
    '09CB4289-207A-42EF-BBC5-3224C6D5D41E', // Harry Li (3160)
    '0617CBCD-E710-465B-B6D4-B4F8AB81E71B', // Brendan Philp (3561)
    'ef662590-041b-41a7-8a62-70729dc0a277', // Aladdin Elmir (710185)
    'e2fbc54b-53ad-447b-8a84-7546ceadd8b2', // Neill Johnson (710263)
    '23E36FA1-E030-42D9-A4C7-4C8B30EDD243', // Tim Palioudis (277169)
    '316bb934-89c7-4212-9a1d-d01892936daa', // Sam Rossello (744514)
    '4E368AAF-E89F-4164-A375-1BE75111E6D6', // Cheeky Dunlop (174537)
    '41D0C6D1-52A4-4573-B225-BAEE999414EA', // Ned Nikolic (3310)
    '63E82ABF-C7F5-4210-9DE9-26D7AD5B3366', // Jason Allen (270125)
    '28AB8CD7-9522-4743-8043-C6F8BF50D47F', // James Hatzimoisis (284175)
    '516e5cc4-0bba-44fd-87e5-ec09ab97b7a1', // Mitch Galea (369583)
    '4DD93B70-691F-4E15-8683-D37A6F350300', // Andrew Oostenbrink (169675)
    'd8ef1d61-727d-4938-a600-a852e4b06fc2', // Mark Somboli (248772)
    '432B1955-E920-423F-9D04-3D920E5C1110', // Vivek Kanwar (172367)
    '8E495B9F-B10E-42FF-846E-943A9EB1C5D3', // Tristan Tomasino (3180)
    '6574B795-B37A-4262-971A-ED6DD475F0EE', // Nickolas Dilles (3263)
    '68068100-4AA8-4BEC-AC05-61CE335BD3D8', // Greg Andison (3213)
    'C463F21B-09B2-4E86-A121-4721A4CB6815', // Penny Stylianou (704)
    'FFEF0D18-0A99-4E4F-9DF7-7DDCCEE8BE01', // Craig Seymour (273758)
    'C2C18ADC-EDD3-4FE0-AD2D-B37DB8602E7B', // John Kubatov (641)
    '49BDBA47-0F40-4E0E-89E8-D6BF146ECF43', // Brent May
    'F8B676A3-A407-4F23-A6FC-9C6F1693A8C5', // Gordana Macura
    'E6909C3A-D2D5-481F-9BED-C1FA29B57FDC', // Simon Nakhla
  ].includes(state.user.key),
  },
}));

export default userStore;
